import { useEffect, useState, useCallback } from "react";
import { Camera, Trash2, X } from "lucide-react";
import { API, apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const PhotoWall = ({ eventId, isPast }) => {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(() => {
    apiFetch(`/events/${eventId}/photos`).then((r) => setPhotos(r.photos)).catch(() => {});
  }, [eventId]);

  useEffect(load, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    form.append("caption", caption);
    try {
      const res = await fetch(`${API}/events/${eventId}/photos`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Upload failed");
      toast.success("Photo added to the wall");
      setCaption("");
      load();
    } catch (err) {
      toast.error(err.message);
    }
    setBusy(false);
    e.target.value = "";
  };

  const remove = async (photo) => {
    try {
      await apiFetch(`/photos/${photo.photo_id}`, { method: "DELETE" });
      toast.success("Photo removed");
      setLightbox(null);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="mt-10 border-t border-neutral-200 pt-8" data-testid="photo-wall">
      <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-neutral-900">
        <Camera className="h-5 w-5 text-[#4D7C0F]" /> Photo wall
      </h2>
      <p className="mt-1 text-sm text-neutral-500">
        {isPast
          ? "Shots from the night — add yours so the calendar keeps a history."
          : "Photos land here after the event."}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          data-testid="photo-caption"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption (optional)"
          className="min-w-[220px] flex-1 rounded-full border border-neutral-300 px-4 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900"
        />
        <label className="flex cursor-pointer items-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-neutral-700">
          <Camera className="h-4 w-4" /> {busy ? "Uploading…" : "Add photo"}
          <input
            type="file"
            accept="image/*"
            onChange={upload}
            className="hidden"
            data-testid="photo-upload-input"
          />
        </label>
      </div>

      {photos.length === 0 ? (
        <p className="mt-6 rounded-2xl border border-dashed border-neutral-300 p-10 text-center text-sm text-neutral-500" data-testid="photo-empty">
          No photos yet.
        </p>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="photo-grid">
          {photos.map((p) => (
            <button
              key={p.photo_id}
              onClick={() => setLightbox(p)}
              data-testid={`photo-${p.photo_id}`}
              className="group relative overflow-hidden rounded-xl bg-neutral-100 aspect-square"
            >
              <img
                src={`${API}/photos/${p.photo_id}/image`}
                alt={p.caption || "Event photo"}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {p.caption && (
                <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-2 py-2 text-left text-[11px] font-semibold text-white">
                  {p.caption}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setLightbox(null)}>
          <div className="max-h-full max-w-3xl" onClick={(e) => e.stopPropagation()} data-testid="photo-lightbox">
            <img
              src={`${API}/photos/${lightbox.photo_id}/image`}
              alt={lightbox.caption || "Event photo"}
              className="max-h-[75vh] rounded-xl object-contain"
            />
            <div className="mt-3 flex items-center justify-between gap-4 text-white">
              <p className="text-sm">
                {lightbox.caption ? `${lightbox.caption} · ` : ""}
                <span className="text-white/70">by {lightbox.author.name}</span>
              </p>
              <div className="flex gap-2">
                {(lightbox.author.user_id === user?.user_id || user?.role === "admin") && (
                  <button
                    onClick={() => remove(lightbox)}
                    data-testid="photo-delete"
                    className="grid h-9 w-9 place-items-center rounded-full bg-white/15 transition-colors duration-200 hover:bg-white/25"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => setLightbox(null)}
                  data-testid="photo-close"
                  className="grid h-9 w-9 place-items-center rounded-full bg-white/15 transition-colors duration-200 hover:bg-white/25"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
